import {chunk} from 'lodash';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {genericMandarkRequest} from 'resources/mandark.resource';

type GenericRequestQuery = Parameters<typeof genericMandarkRequest>[1];
import {List, type List as ImmutableList, type Map as ImmutableMap} from 'immutable';
import sessionStore from 'client/Session/SessionStore';

import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import notifier from '@albert-io/notifier';

export const ASSIGNMENT_BUILDER_LAZY_RECIPIENTS_FLAG = 'assignment_builder_lazy_recipients';

interface TeacherModel {
  getMeta: () => {
    getSchoolLicensesInfo: () => ImmutableList<ImmutableMap<string, unknown>>;
  };
}

/**
 * The assignment read that backs every builder route.
 *
 * `omitStudents` drops the two student includes, which for an assignment with
 * thousands of assigned students are the whole payload — and the whole freeze
 * (#21182). Routes that render no per-student UI pass it; the settings route
 * does not, because its form is built from the roster. In exchange the
 * classroom meta gains this assignment's context, which populates the
 * per-classroom assigned/override/in-progress counts.
 */
export const getAssignmentQuery = (
  assignmentId: string,
  isAdmin: boolean,
  isDraft: boolean,
  signal?: AbortSignal,
  {omitStudents = false}: {omitStudents?: boolean} = {}
) => {
  const modelType = isDraft
    ? {endpoint: 'draft_assignments_v1', metaEndpoint: 'draft_assignment_v1'}
    : {endpoint: 'assignments_v3', metaEndpoint: 'assignment_v3'};
  const teacherId = masqueradeStore.getUserIdByMasqueradeState();

  const isViewingAsAdmin = isAdmin && teacherId === sessionStore.getUserId();

  // While masquerading, the acting admin can only read classrooms at the school
  // they masqueraded into. A teacher who teaches at multiple schools would
  // otherwise pull in classrooms at schools the admin doesn't administrate,
  // which trips the include layer's all-or-nothing read gate (403). Scope the
  // classroom/student includes to the masquerade school so only readable
  // classrooms come back.
  const isMasquerading = masqueradeStore.isMasquerading();
  const masqueradeSchoolId = masqueradeStore.getSchoolId();
  const masqueradeSchoolFilter =
    isMasquerading && masqueradeSchoolId ? {school_v5: masqueradeSchoolId} : {};

  let baseResource = resource(modelType.endpoint)
    .mandarkEndpoint([modelType.endpoint, assignmentId])
    .include('question_sets_v1.questions_v3')
    .include('question_sets_v1.guide_levels_v2', teacherId)
    .include('question_sets_v1.subject_v2', teacherId)
    .include('classrooms_v1.school_v5')
    .include('teacher_v1')
    .include('classrooms_v1.teachers_v1.school_v5')
    .include('question_sets_v1.tags_v1')
    .include('question_sets_v1.questions_v3'); // added to get access to question's status

  if (!omitStudents) {
    baseResource = baseResource
      .include('classrooms_v1.students_v2.student_assignments_v1')
      .include('students_v2');
  }

  if (!isDraft) {
    // For published assignments, add both classroom_v1 and assignment_v3 meta to receive assignment stats
    baseResource = baseResource.withMeta('classroom_v1,assignment_v3').meta({
      context: {
        has_lti_course: true,
        lti_deployment: true,
        lti_platform_name: true,
        assignment: {
          stats: true,
          // The per-classroom assigned/override/in-progress counts are gated on this context.
          ...(omitStudents && {id: assignmentId})
        }
      }
    });
  } else {
    // For draft assignments, only add classroom_v1 meta
    baseResource = baseResource.withMeta('classroom_v1').meta({
      context: {
        has_lti_course: true,
        lti_deployment: true,
        lti_platform_name: true,
        // Drafts hold their recipients in `student_assignments` exactly like published
        // assignments do, so the counts compute for them too — without this context every
        // draft class row reads "All future students" no matter how many are assigned.
        ...(omitStudents && {assignment: {id: assignmentId}})
      }
    });
  }

  const adminReadableScope = {
    school_v5: {
      school_personnel_v1: {
        teacher_v1: {
          id: teacherId
        },
        role_v1: {
          name: {
            not: 'teacher'
          }
        },
        status: 'approved'
      }
    }
  };

  const classroomScope = isViewingAsAdmin
    ? {
        any_of: [{teachers_v1: teacherId}, adminReadableScope]
      }
    : {
        teachers_v1: teacherId,
        ...masqueradeSchoolFilter
      };

  // Every student filter here scopes an include that `omitStudents` removed. The
  // include layer's read gate is all-or-nothing, so a filter on an absent include
  // is not merely redundant — leave them off together.
  const studentScopes = omitStudents
    ? {}
    : {
        students_v2: isViewingAsAdmin
          ? {
              any_of: [
                {classrooms_v1: {teachers_v1: teacherId}},
                {classrooms_v1: adminReadableScope}
              ]
            }
          : {
              classrooms_v1: {
                teachers_v1: teacherId,
                ...masqueradeSchoolFilter
              }
            },
        'classrooms_v1.students_v2.student_assignments_v1': {
          id: assignmentId,
          ...(isViewingAsAdmin ? {} : {teacher_v1: teacherId})
        }
      };

  let filteredResource = baseResource;

  if (sessionStore.isSuper()) {
    // A super user's only scope here is the student-assignment one, so with the
    // students gone there is nothing left to filter.
    if (!omitStudents) {
      filteredResource = baseResource.filter({
        included: {
          'classrooms_v1.students_v2.student_assignments_v1': {
            id: assignmentId
          }
        }
      });
    }
  } else {
    filteredResource = baseResource.filter({
      included: {
        classrooms_v1: classroomScope,
        ...studentScopes
      }
    });
  }

  if (isViewingAsAdmin && !sessionStore.isSuper() && !isDraft) {
    filteredResource = filteredResource
      .meta({
        context: {
          user: {
            id: masqueradeStore.getUserIdByMasqueradeState(),
            is_creator_or_classroom_teacher: true
          }
        }
      })
      .filter({
        'meta.user_is_creator_or_classroom_teacher': true
      });
  }

  return genericMandarkRequest(
    'get',
    filteredResource.done() as GenericRequestQuery,
    {},
    {abortSignal: signal, suppressGlobalErrorToast: true}
  );
};

/**
 * How many students one expand-a-class page pulls. Small enough that the main thread never
 * stalls on hydration, large enough that a normal class arrives in a single round trip.
 */
export const LAZY_STUDENTS_PAGE_SIZE = 100;

/**
 * A deterministic order is required, not cosmetic: pagination that ties on last name would
 * otherwise be free to shuffle rows between pages, which shows up as a student appearing twice
 * or not at all. The id breaks every remaining tie.
 */
const LAZY_STUDENTS_SORT = 'last_name,first_name,id';

/**
 * The classroom's masquerade-scoped read path. While masquerading, an admin may only read at the
 * school they masqueraded into — the same scoping the assignment query's includes carry.
 */
const classroomStudentsEndpoint = (classroomId: string) => [
  'classrooms_v1',
  classroomId,
  'students_v2'
];

const masqueradeStudentFilter = () => {
  const schoolId = masqueradeStore.isMasquerading() ? masqueradeStore.getSchoolId() : null;
  return schoolId ? {classrooms_v1: {school_v5: schoolId}} : {};
};

/**
 * The stored per-student settings for one page of students.
 *
 * These are join-table columns, and the API only ever serves them one way: as `meta` on the
 * resource identifiers inside `assignments_v3.relationships.students_v2` — which the parser files
 * on each student as `relationshipMeta.assignment[assignmentId]`, the slot the settings getters
 * have always read. They are NOT reachable from the student side. `student_assignments_v1` comes
 * back empty as an include from a student primary, from a classroom-students primary, and from
 * its own related link, so the page read above can never carry them however it is filtered.
 *
 * Hence one extra request per page, narrowed by `filter[included][students_v2][id]` to just the
 * ids on that page — so the payload stays proportional to what is on screen rather than to the
 * class, which is the whole point of the lazy read.
 *
 * Because the include is the assignment's own `students_v2`, the same read doubles as the
 * membership check: an id asked for that does not come back is not assigned.
 *
 * A draft must be read from `draft_assignments_v1`: `assignments_v3/:id` 404s for one, which the
 * list surfaces as "We couldn't load this class's students" for every class that has any.
 */
export const getStudentAssignmentSettingsQuery = ({
  assignmentId,
  studentIds,
  isDraft = false
}: {
  assignmentId: string;
  studentIds: string[];
  isDraft?: boolean;
}) =>
  resource(isDraft ? 'draft_assignments_v1' : 'assignments_v3')
    .mandarkEndpoint([isDraft ? 'draft_assignments_v1' : 'assignments_v3', assignmentId])
    .include('students_v2')
    .filter({
      included: {
        students_v2: {
          id: studentIds.join(',')
        }
      }
    });

/**
 * The API clamps a page to this many rows. Asking for more does NOT error — it silently returns
 * 250 — so the page walk below must never treat "fewer rows than I asked for" as "last page".
 */
export const IDS_ONLY_PAGE_SIZE = 250;

/**
 * One page of a class's roster (#21182), no includes, so no model hydration cost worth speaking
 * of. The student list reads it `LAZY_STUDENTS_PAGE_SIZE` at a time to offer the whole class;
 * the bulk gestures walk it for bare ids. It carries no membership and no stored settings —
 * both come from `getStudentAssignmentSettingsQuery` — unless `assignmentId` restricts the page
 * to the assignment's current members.
 */
export const getClassroomStudentsQuery = ({
  classroomId,
  assignmentId,
  page = 1,
  pageSize
}: {
  classroomId: string;
  /** Present to restrict to the assignment's students; omitted for the full roster. */
  assignmentId?: string;
  page?: number;
  /** Named by every caller: the list and the id walk want different sizes. */
  pageSize: number;
}) =>
  resource('students_v2')
    .mandarkEndpoint(classroomStudentsEndpoint(classroomId))
    .filter({
      ...(assignmentId ? {student_assignments_v1: assignmentId} : {}),
      ...masqueradeStudentFilter()
    })
    .sort(LAZY_STUDENTS_SORT)
    .page(page)
    .pageSize(pageSize);

/**
 * Every id for a class, walked page by page. Only the two bulk gestures need it: "Include All"
 * has to name the whole roster and "Remove All" everyone currently assigned. Bare UUIDs parse
 * fine at any size — it is model hydration, not the id list, that freezes the tab.
 *
 * The walk stops on an EMPTY page rather than on a short one. A short page is not a reliable end
 * marker here: the API clamps the page size, so a request for more rows than it allows comes back
 * short on the very first page. Reading that as "the end" is how a Remove All silently unassigns
 * only the first page's worth of students and leaves the rest — a wrong membership write that
 * looks like a successful save.
 */
export const fetchAllClassroomStudentIds = async ({
  classroomId,
  assignmentId,
  pageSize = IDS_ONLY_PAGE_SIZE,
  maxPages = 100
}: {
  classroomId: string;
  assignmentId?: string;
  pageSize?: number;
  maxPages?: number;
}): Promise<string[]> => {
  const ids = new global.Set<string>();

  for (let page = 1; page <= maxPages; page++) {
    const students = (await getClassroomStudentsQuery({
      classroomId,
      assignmentId,
      page,
      pageSize
      // Pages must be walked in order; the API has no bulk-ids endpoint
    }).getResourcePromise()) as ImmutableList<{getId: () => string}>;

    if (!students?.size) {
      return [...ids];
    }

    const before = ids.size;
    students.forEach((student) => {
      const id = student?.getId?.();
      if (id) {
        ids.add(id);
      }
    });

    // A page that adds nothing new means pagination is not advancing; continuing would spin
    // until the page ceiling and still return a short list.
    if (ids.size === before) {
      return [...ids];
    }
  }

  // Past the ceiling the list would be silently short, and a short list on a remove-all means
  // students staying assigned that the teacher asked to remove. Fail loudly instead.
  throw new Error(
    `fetchAllClassroomStudentIds: classroom ${classroomId} exceeded ${maxPages} pages of ${pageSize}`
  );
};

// Kept well under the API's 250-row page so 100 UUIDs keep the URL short.
const CALCULATOR_TIER_PAGE_SIZE = 100;

/**
 * Each question's calculator tier from its content (`meta.effective_calculator_type`), which Mandark
 * resolves. One query per page of question sets. Mandark bridge: GraphQL has no question-level
 * effective calculator type.
 */
export const getQuestionCalculatorTiersQueries = (questionSetIds: string[]) =>
  chunk(questionSetIds, CALCULATOR_TIER_PAGE_SIZE).map((ids) =>
    resource('question_sets_v1')
      .mandarkEndpoint(['question_sets_v1'])
      .filter({id: ids.join(',')})
      .include('questions_v3')
      .fields({question_set_v1: 'questions_v3', question_v3: 'status'})
      .withMeta('question_v3')
      .customQuery({meta: {context: {effective_calculator_type: true}}})
      .pageSize(ids.length)
  );

// Record-only readability probe (#19162): the bare read hits only the assignment's read authz, so a
// 200 means the block was a content-license 403 (upgrade unlocks it), a 403 means an access wall.
export const getAssignmentRecordQuery = (
  assignmentId: string,
  isDraft: boolean,
  signal?: AbortSignal
) => {
  const endpoint = isDraft ? 'draft_assignments_v1' : 'assignments_v3';
  return genericMandarkRequest(
    'get',
    resource(endpoint).mandarkEndpoint([endpoint, assignmentId]).done() as GenericRequestQuery,
    {},
    {abortSignal: signal, suppressGlobalErrorToast: true}
  );
};

export const getTeacherSchools = async (teacherId: string) => {
  const query = getTeacherWithMetadataQuery(teacherId);
  try {
    const teacher = (await query.getResourcePromise()) as TeacherModel;
    const schoolLicensesInfo = teacher.getMeta?.()?.getSchoolLicensesInfo?.();
    if (!schoolLicensesInfo) {
      // Unexpected meta shape (shape change / partial fetch): report it so a real
      // data failure is distinguishable from "teacher has no school licenses".
      notifier.notify(
        new Error('getTeacherSchools: teacher meta is missing getSchoolLicensesInfo'),
        {
          message: 'Unexpected teacher meta shape when fetching school licenses',
          tags: 'content-discovery'
        }
      );
      return List([]);
    }
    const schoolPromises = schoolLicensesInfo
      .filter((licenseInformation) => {
        const hasSchoolLicense = licenseInformation?.get('has_school_license');
        const licenseStatus = licenseInformation?.get('school_license_status');
        return Boolean(hasSchoolLicense) && licenseStatus === 'active';
      })
      .map((approvedlicenseInformation) =>
        getSchoolByIdQuery(approvedlicenseInformation?.get('school_id'))
      )
      .toArray();
    const schools = await Promise.all(schoolPromises);
    return List(schools);
  } catch (_err) {
    return List([]);
  }
};

/**
 * The API caps a page at this many rows and does NOT error when asked for more — it silently
 * returns 250. Reading past it in one page would drop the surplus schools, which is
 * indistinguishable from a school that really has no upgrade.
 */
const SCHOOLS_PAGE_SIZE = 250;

/**
 * License-bearing copies of named schools, for the Pro checks that gate Secure Mode.
 *
 * Pro coverage belongs to the class's school, but `getTeacherSchools` only names schools the
 * acting user is personnel at — so a super inspecting someone else's assignment read every class
 * as non-Pro (#21182). Reading a school one is not personnel at is a super-only capability (the
 * collection read is all-or-nothing and 403s for everyone else), so callers ask only for them.
 *
 * A refused page resolves empty, leaving the caller's own schools intact; any other failure is
 * reported, because silently reading as "no Pro" is what makes the settings form scrub a stored
 * Secure Mode on the next save.
 */
export const getSchoolsWithLicenses = async (schoolIds: string[]) => {
  if (!schoolIds.length) {
    return List([]);
  }

  const chunks: string[][] = [];
  for (let index = 0; index < schoolIds.length; index += SCHOOLS_PAGE_SIZE) {
    chunks.push(schoolIds.slice(index, index + SCHOOLS_PAGE_SIZE));
  }

  const reads = await Promise.all(
    chunks.map(async (ids) => {
      try {
        const schools = await resource('school_v5')
          .mandarkEndpoint(['schools_v5'])
          .include('school_licenses_v1.upgrades_v1')
          .filter({id: ids.join(',')})
          .pageSize(ids.length)
          .getResourcePromise();
        return List.isList(schools) ? schools : List([]);
      } catch (err) {
        if ((err as {status?: number})?.status !== 403) {
          notifier.notify(err as Error, {
            message: 'Failed to read school licenses for the assignment builder',
            tags: 'content-discovery'
          });
        }
        return List([]);
      }
    })
  );

  return List(reads).flatten(1) as List<unknown>;
};

// imported from marketplace/src/client/AccountSettings/Subscriptions/InstructionalLicenseCard.js
const getSchoolByIdQuery = (id) => {
  return resource('school_v5')
    .mandarkEndpoint(['schools_v5', id])
    .include('school_licenses_v1.upgrades_v1')
    .getResourcePromise();
};

const getTeacherWithMetadataQuery = (teacherId) => {
  return resource('teachers_v1')
    .mandarkEndpoint(['teachers_v1', teacherId])
    .include('school_v5')
    .include('school_personnel_v1')
    .include('school_personnel_v1.school_v5')
    .withMeta('teacher_v1')
    .customQuery({
      meta: {
        context: {
          teacher: {
            license_info: true
          }
        }
      }
    });
};
